import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { streamChat } from "@/lib/streaming";
import { apiUrl } from "@/lib/api";
import type { Citation } from "@/types/api";
import { cn } from "@/lib/utils";

type Message = {
  id: string;
  role: "user" | "assistant";
  text: string;
  citations?: Citation[];
  source?: "ollama" | "claude";
  pending?: boolean;
};

export function ChatPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  async function send() {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);

    const userId = crypto.randomUUID();
    const assistantId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", text: q },
      { id: assistantId, role: "assistant", text: "", pending: true },
    ]);

    abortRef.current = new AbortController();
    await streamChat(q, abortRef.current.signal, {
      onEvent: (e) => {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantId) return m;
            switch (e.kind) {
              case "token":
                return { ...m, text: m.text + e.text };
              case "citations":
                return { ...m, citations: e.citations };
              case "meta":
                return { ...m, source: e.source };
              case "done":
                return { ...m, pending: false };
              case "error":
                return { ...m, pending: false, text: m.text || `Error: ${e.message}` };
              default:
                return m;
            }
          }),
        );
      },
      onError: (err) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, pending: false, text: `Error: ${err.message}` } : m,
          ),
        );
      },
    });

    setBusy(false);
    abortRef.current = null;
  }

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="mx-auto flex max-w-2xl flex-col gap-4">
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
          </div>
        )}
      </div>
      <form
        className="border-t bg-background p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <div className="mx-auto flex max-w-2xl items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about your documents…"
            disabled={busy}
            autoFocus
            inputMode="text"
            enterKeyHint="send"
          />
          <Button type="submit" size="icon" disabled={busy || !input.trim()} aria-label="Send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-3 text-center">
      <Sparkles className="h-10 w-10 text-muted-foreground" />
      <h2 className="text-lg font-semibold">Ask the vault</h2>
      <p className="text-sm text-muted-foreground">
        Try: <span className="italic">“when does our car insurance expire?”</span> or{" "}
        <span className="italic">“what did the pediatrician say about the rash?”</span>
      </p>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === "user";
  return (
    <div className={cn("flex flex-col gap-1", isUser ? "items-end" : "items-start")}>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm",
          isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
        )}
      >
        {message.text || (message.pending ? <span className="animate-pulse">…</span> : null)}
      </div>
      {!isUser && (message.source || message.citations?.length) ? (
        <div className="flex flex-wrap items-center gap-1 pl-1">
          {message.source ? (
            <Badge variant="secondary" className="text-[10px]">
              answered by {message.source}
            </Badge>
          ) : null}
          {message.citations?.map((c) => (
            <a
              key={c.chunkId}
              href={apiUrl(c.signedUrl)}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-accent"
            >
              {c.documentTitle ?? "source"}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
